import difference from "lodash/difference";
import intersection from "lodash/intersection";
import isEmpty from "lodash/isEmpty";
import { Row } from "PFApp/booking/types";
import { CalendarRoot } from "PFApp/booking/types/calendar_root";
import { RowType } from "PFApp/booking/types/row";
import { BulkOperationFilter } from "PFCore/services/bookings_bulk_operation";
import { Id } from "PFTypes";

import { SelectModeData } from "../booking_edit_mode_store.types";

export const removeBookingFromFilters = (
  filters: BulkOperationFilter[],
  bookingId: Id
): BulkOperationFilter[] =>
  filters.flatMap((filter) => {
    const filteredIds = filter.id?.filter((id) => id !== bookingId);
    if (!filteredIds || filteredIds.length === 0) {
      return [];
    }
    return [{ ...filter, id: filteredIds }];
  }) as BulkOperationFilter[];

export const removeBookingFromExclude = (
  exclude: SelectModeData["exclude"],
  bookingId: Id
): SelectModeData["exclude"] => {
  const newExclude = exclude?.id?.filter((id) => id !== bookingId) ?? [];
  return newExclude.length > 0 ? { id: newExclude } : undefined;
};

export const removeActivityProfileFilter = (
  filters: BulkOperationFilter[],
  activityIdToRemove: Id,
  profileIdToRemove?: Id
): BulkOperationFilter[] =>
  profileIdToRemove
    ? filters.filter(
        ({ activityId, profileId }) => activityIdToRemove !== activityId || profileIdToRemove !== profileId
      )
    : filters.filter(({ activityId }) => activityIdToRemove !== activityId);

export const getBookingIdsFromFilters = (selection: SelectModeData | null): Id[] => {
  if (!selection) {
    return [];
  }
  return selection.filters.flatMap((filter) => filter.id ?? []);
};

export const areAllBookingsSelected = (
  bookingIds: Id[],
  selection: SelectModeData | null,
  isSelectAllMode: boolean,
  isRowSelected: boolean
): boolean => {
  const selectedBookingIds = getBookingIdsFromFilters(selection);
  const areAllBookingsSelectedByBookingIds = difference(bookingIds, selectedBookingIds).length === 0;

  if (isSelectAllMode) {
    return areAllBookingsSelectedByBookingIds;
  } else {
    return (
      areAllBookingsSelectedByBookingIds ||
      (isRowSelected && isEmpty(intersection(bookingIds, selection?.exclude?.id)))
    );
  }
};

export const isRowSelected = (row: Row, selectedRows: Row[]): boolean => {
  if (selectedRows.length === 0) {
    return false;
  }

  const selectedKeys = selectedRows.map(({ key }) => key);
  const isDirectlySelected = selectedKeys.includes(row.key);
  const isChildOfSelected = row.parents.some((parent) => selectedKeys.includes(parent));

  return isDirectlySelected || isChildOfSelected;
};

export const isChildRowSelected = (row: Row, selectedRows: Row[]): boolean =>
  !!selectedRows.some(({ parents }) => parents.includes(row.key));

type UnselectRowBookingsParams = {
  bookingIds: Id[];
  currentSelection: SelectModeData;
  isSelectAllMode: boolean;
};

type UnselectRowBookingsResult = {
  strategy: "addToExclude" | "removeFromFilters";
  newSelection?: SelectModeData;
  bookingsToExclude?: Id[];
};

export const computeUnselectRowBookings = (params: UnselectRowBookingsParams): UnselectRowBookingsResult => {
  const { bookingIds, currentSelection, isSelectAllMode } = params;

  if (isSelectAllMode) {
    const newFilters = currentSelection.filters
      .map((filter) => {
        if (filter?.id) {
          const newFilteredIds = difference(filter.id, bookingIds);
          return {
            ...filter,
            id: newFilteredIds
          };
        }
        return filter;
      })
      .filter((filter) => (filter.id && filter.id.length > 0) || filter.activityId || filter.profileId);

    return {
      strategy: "removeFromFilters",
      newSelection: {
        ...currentSelection,
        filters: newFilters
      }
    };
  } else {
    return {
      strategy: "addToExclude",
      bookingsToExclude: bookingIds
    };
  }
};

type RowSelectionFiltersParams = {
  rowType: RowType;
  rowId: Id;
  calendarRoot: CalendarRoot;
  parentIds: Id[];
  isLinkedToEngagement: boolean;
  isAllBookingsModeEnabled: boolean;
  bookingCategoryIds?: Id[];
};

export const computeRowSelectionFilters = ({
  rowType,
  rowId,
  calendarRoot,
  parentIds,
  isLinkedToEngagement,
  isAllBookingsModeEnabled,
  bookingCategoryIds
}: RowSelectionFiltersParams): BulkOperationFilter => {
  if (calendarRoot === CalendarRoot.ProjectsEngagements || calendarRoot === CalendarRoot.ProjectsRoles) {
    if (rowType === RowType.Profile) {
      const closestParentId = parentIds[parentIds.length - 1];

      return {
        profileId: rowId,
        ...(isAllBookingsModeEnabled ? {} : { activityId: closestParentId }),
        includeChildren: !isLinkedToEngagement
      };
    } else {
      return {
        activityId: rowId,
        includeChildren: true
      };
    }
  }

  if (calendarRoot === CalendarRoot.WorkforceProfiles) {
    const isProfileRow = rowType === RowType.Profile;
    const isCategoryRow = rowType === RowType.Category;
    const isActivityRow = [RowType.Role, RowType.Engagement].includes(rowType);

    const rootParentProfileId = parentIds[0];
    const profileId = isProfileRow ? rowId : rootParentProfileId;

    if (isActivityRow) {
      return {
        activityId: rowId,
        profileId
      };
    }

    if (isCategoryRow) {
      return {
        bookingCategoryId: bookingCategoryIds,
        profileId
      };
    }

    return {
      profileId
    };
  }

  return {};
};
